import { useState, useEffect, useRef, useCallback } from 'react';
import UploadPage      from './components/UploadPage';
import JobDashboard   from './components/JobDashboard';
import ResultsViewer  from './components/ResultsViewer';
import GrafanaLink    from './components/GrafanaLink';
import AuthForm       from './components/AuthForm';
import RegressionView from './components/RegressionView';
import RulesEditor    from './components/RulesEditor';
import AnomalyDashboard      from './components/AnomalyDashboard';
import PredictiveMaintenance from './components/PredictiveMaintenance';
import EC2Dashboard          from './components/EC2Dashboard';
import ModeIndicator  from './components/ModeIndicator';
import './App.css';

const AUTH_BASE    = process.env.REACT_APP_AUTH_API_URL
  || process.env.REACT_APP_API_URL
  || 'https://<api-id>.execute-api.us-east-1.amazonaws.com/prod';

const IDLE_TIMEOUT_MS  = 60 * 60 * 1000;  // 1 hour — logout if no activity
const WARN_BEFORE_MS   = 2  * 60 * 1000;  // show warning 2 min before logout
const HEARTBEAT_MS     = 5  * 60 * 1000;  // ping server every 5 min while active

export default function App() {
  const [session,        setSession]        = useState(null);
  const [authChecked,    setAuthChecked]    = useState(false);
  const [page,           setPage]           = useState('upload');
  const [highlightJobId, setHighlightJobId] = useState(null);
  const [viewJobId,      setViewJobId]      = useState(null);
  const [showWarning,    setShowWarning]    = useState(false);

  const idleTimer     = useRef(null);
  const warnTimer     = useRef(null);
  const heartbeatRef  = useRef(null);
  const sessionRef    = useRef(session);  // always current in event listeners

  // Keep ref in sync
  useEffect(() => { sessionRef.current = session; }, [session]);

  // ── Logout ──────────────────────────────────────────────────────────────────
  const logout = useCallback((reason = 'manual') => {
    clearTimeout(idleTimer.current);
    clearTimeout(warnTimer.current);
    clearInterval(heartbeatRef.current);
    localStorage.removeItem('biw_session');
    setSession(null);
    setShowWarning(false);
    if (reason === 'idle') {
      alert('You have been signed out due to 1 hour of inactivity.');
    }
  }, []);

  // ── Reset idle timer on any activity ────────────────────────────────────────
  const resetIdle = useCallback(() => {
    if (!sessionRef.current) return;
    setShowWarning(false);
    clearTimeout(idleTimer.current);
    clearTimeout(warnTimer.current);

    // Show warning 2 min before logout
    warnTimer.current = setTimeout(() => {
      setShowWarning(true);
    }, IDLE_TIMEOUT_MS - WARN_BEFORE_MS);

    // Auto-logout after full idle period
    idleTimer.current = setTimeout(() => {
      logout('idle');
    }, IDLE_TIMEOUT_MS);
  }, [logout]);

  // ── Heartbeat — extends server-side session while user is active ─────────────
  const startHeartbeat = useCallback((token) => {
    clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(async () => {
      if (!sessionRef.current) return;
      try {
        const res = await fetch(`${AUTH_BASE}/auth/heartbeat`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ token }),
        });
        const data = await res.json();
        if (!data.ok) {
          // Server says session expired — force logout
          logout('idle');
        } else {
          // Update local expiresAt from server response
          const stored = localStorage.getItem('biw_session');
          if (stored) {
            const s = JSON.parse(stored);
            s.expiresAt = data.expiresAt;
            localStorage.setItem('biw_session', JSON.stringify(s));
          }
        }
      } catch {
        // Network error — keep session alive locally
      }
    }, HEARTBEAT_MS);
  }, [logout]);

  // ── Restore session on mount ─────────────────────────────────────────────────
  useEffect(() => {
    const stored = localStorage.getItem('biw_session');
    if (!stored) { setAuthChecked(true); return; }
    const s = JSON.parse(stored);
    if (s.expiresAt * 1000 < Date.now()) {
      localStorage.removeItem('biw_session');
      setAuthChecked(true);
      return;
    }
    fetch(`${AUTH_BASE}/auth/validate-session?token=${s.token}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) setSession(s);
        else localStorage.removeItem('biw_session');
      })
      .catch(() => setSession(s))
      .finally(() => setAuthChecked(true));
  }, []);

  // ── Start idle tracking when session is set ──────────────────────────────────
  useEffect(() => {
    if (!session) {
      clearTimeout(idleTimer.current);
      clearTimeout(warnTimer.current);
      clearInterval(heartbeatRef.current);
      return;
    }
    resetIdle();
    startHeartbeat(session.token);

    // Activity events that reset the idle timer
    const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    EVENTS.forEach(e => window.addEventListener(e, resetIdle, { passive: true }));

    return () => {
      EVENTS.forEach(e => window.removeEventListener(e, resetIdle));
      clearTimeout(idleTimer.current);
      clearTimeout(warnTimer.current);
      clearInterval(heartbeatRef.current);
    };
  }, [session, resetIdle, startHeartbeat]);

  const handleAuthenticated = (s) => {
    localStorage.setItem('biw_session', JSON.stringify(s));
    setSession(s);
  };

  const handleJobCreated  = (jobId) => { setHighlightJobId(jobId); setPage('jobs'); };
  const handleViewResults = (jobId) => { setViewJobId(jobId); setPage('results'); };

  if (!authChecked) return null;
  if (!session)     return <AuthForm onAuthenticated={handleAuthenticated} />;

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9' }}>

      {/* ── Idle warning banner ── */}
      {showWarning && (
        <div style={styles.warnBanner}>
          <span>⚠️ You will be signed out in 2 minutes due to inactivity.</span>
          <button onClick={resetIdle} style={styles.warnBtn}>Stay signed in</button>
        </div>
      )}

      {/* ── Nav ── */}
      <nav style={styles.nav}>
        <span style={styles.brand}>🏭 BIW Validation Portal</span>
        <div style={styles.navLinks}>
          {[['upload','⬆ Upload'],['jobs','📋 Jobs'],['results','📊 Results'],['regression','📈 Regression'],['anomaly','🤖 Anomaly'],['maintenance','🔧 Maintenance'],['ec2','🖥️ EC2 Fleet'],['grafana','📡 Grafana']].map(([p, label]) => (
            <button key={p} onClick={() => setPage(p)}
              style={{ ...styles.navBtn, fontWeight: page === p ? 700 : 400, borderBottom: page === p ? '2px solid #fff' : 'none' }}>
              {label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ModeIndicator session={session} />
          <span style={{ color: '#cce', fontSize: 13 }}>{session.fullName} ({session.email})</span>
          <button onClick={() => logout('manual')} style={styles.logoutBtn}>Sign out</button>
        </div>
      </nav>

      {/* ── Page content ── */}
      <main style={{ padding: '24px 16px' }}>
        {page === 'upload'     && <UploadPage    onJobCreated={handleJobCreated} session={session} />}
        {page === 'jobs'       && <JobDashboard  highlightJobId={highlightJobId} onViewResults={handleViewResults} />}
        {page === 'results'    && <ResultsViewer jobId={viewJobId} onBack={() => setPage('jobs')} />}
        {page === 'regression' && <RegressionView />}
        {page === 'anomaly'    && <AnomalyDashboard />}
        {page === 'maintenance' && <PredictiveMaintenance />}
        {page === 'ec2'         && <EC2Dashboard session={session} />}
        {page === 'grafana'    && <GrafanaLink />}
      </main>
    </div>
  );
}

const styles = {
  nav:        { background: '#0073bb', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  brand:      { color: '#fff', fontWeight: 700, fontSize: 18, fontFamily: 'sans-serif' },
  navLinks:   { display: 'flex', gap: 8 },
  navBtn:     { background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14, padding: '4px 12px' },
  logoutBtn:  { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 13 },
  warnBanner: { background: '#e67e22', color: '#fff', padding: '10px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14 },
  warnBtn:    { background: '#fff', color: '#e67e22', border: 'none', borderRadius: 4, padding: '6px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
};
